import React, { useEffect } from 'react';
import { VStack, Heading, Text, Button, HStack, Box } from '@chakra-ui/react';
import { useGame } from '../../contexts/GameContext.tsx';
import { useSound } from '../../contexts/SoundContext.tsx';
import { Player } from '../../types/game.ts';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import PlayerName from '../Common/PlayerName.tsx';
import { auth } from '../../config/firebase.ts';

const MotionBox = motion(Box);

interface GameOverProps {
  onBackToLobby?: () => void;
}

const GameOver: React.FC<GameOverProps> = ({ onBackToLobby }) => {
  const { t } = useTranslation();
  const { state, resetGame } = useGame();
  const { playSound } = useSound();
  const winner = state.players.find(player => player.id === state.winner);

  // Jouer le son de victoire au montage
  useEffect(() => {
    playSound('gameWin');
  }, [playSound]);

  return (
    <MotionBox
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      p={{ base: 4, md: 8 }}
      bg="var(--gris-bleute)"
      borderRadius="xl"
      boxShadow="2xl"
      border="1px solid"
      borderColor="rgba(139, 148, 179, 0.2)"
      w="full"
      maxW="800px"
    >
      <VStack spacing={8}>
        <Heading size="xl" color="var(--texte-clair)">
          {t('gameOverTitle')}
        </Heading>
        
        <Heading size="lg" color="var(--bleu-electrique)">
          🏆 {winner ? t('playerWon', { name: winner.name }) : ''} 🏆
        </Heading>

        <HStack spacing={6} align="stretch">
          {state.players.map((player: Player) => (
            <VStack 
              key={player.id} 
              p={6} 
              bg="var(--nuit-profonde)" 
              borderRadius="lg" 
              border="1px solid" 
              borderColor="rgba(139, 148, 179, 0.2)"
              flex="1"
            >
              <Text fontSize="xl" fontWeight="bold" color="var(--texte-clair)">
                <PlayerName
                  userId={auth.currentUser?.uid || ''}
                  userName={player.name}
                  checkEffect={!!auth.currentUser?.uid}
                />
              </Text>
              
              {/* Affichage des points (streak cumulé) */}
              <Text fontSize="lg" fontWeight="bold" color="var(--bleu-electrique)">
                {Object.values(player.score || {}).reduce((total: number, score: any) => total + (Number(score) || 0), 0)} {t('points')}
              </Text>
              
              <Text color="var(--texte-secondaire)">
                {/* CORRECTION ICI : On utilise la variable d'état */}
                {t('validatedCategories')} : {player.completedCategories.filter(catId => {
                    const category = state.categories.find(c => c.id === catId);
                    return category ? !category.is_mystery : false;
                }).length} / {state.categoriesToWin}
              </Text>
              <Text fontSize="sm" color="var(--texte-secondaire)" textAlign="center" minH="40px">
                {player.completedCategories.map((catId: string) => 
                  state.categories.find(c => c.id === catId)?.name
                ).join(', ')}
              </Text>
            </VStack>
          ))}
        </HStack>

        <VStack spacing={4}>
          <Button
            size="lg"
            onClick={resetGame}
            className="quizvs-button"
            w="250px"
          >
            {t('newGameButton')}
          </Button>
          
          <Button
            size="lg"
            onClick={onBackToLobby || (() => window.location.reload())}
            variant="outline"
            colorScheme="blue"
            w="250px"
          >
            {t('backToLobby')}
          </Button>
        </VStack>
      </VStack>
    </MotionBox>
  );
};

export default GameOver;